<script lang="ts">
    import type {DocumentDetailReactivePageData, DocumentDetailStaticPageData} from '../types';
    import {exists} from 'shared/utils/custom-utils';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpDocumentDetailPageService} from '../kp-document-detail-page.service';
    import {getPageContext} from 'shared/layouts/page-context';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpLinksMenu from 'shared/components/kp-links-menu/KpLinksMenu.svelte';
    import KpActionRequestButton from 'shared/components/kp-action-request-button/KpActionRequestButton.svelte';
    import RecordPermalink from '../../record-permalink/RecordPermalink.svelte';

    const localize = getLocalization();
    const service = getInjector().getByClass(KpDocumentDetailPageService);
    const pageContext = getPageContext<DocumentDetailStaticPageData, DocumentDetailReactivePageData>();

    const model = pageContext.staticData.model;

    let coverInputFileList: FileList | undefined;
    $: if (exists(coverInputFileList) && exists(coverInputFileList[0])) handleCoverImageSelect();

    const handleCoverImageSelect = async () => {
        if (exists(coverInputFileList) && exists(coverInputFileList[0])) {
            await service.uploadCoverImage(model.record, coverInputFileList);
        }
    }

    const handleSendByEmailClick = async () => {
        await service.showSendByEmailForm(model.record);
    }

    const handlePrintClick = () => {
        window.print();
    }
</script>

<div class="action-buttons-container">
    <!-- Links and actions container -->
    <div class="links-and-actions-container">
        <!-- Send by E-Mail button -->
        <KpButton additionalClasses="odeslatEmailem" on:click={handleSendByEmailClick}>
            <IconedContent icon="envelope">
                {localize(/* @kp-localization detail.odeslatEmailem */ 'detail.odeslatEmailem')}
            </IconedContent>
        </KpButton>

        <!-- Print button -->
        <KpButton additionalClasses="tisknout" on:click={handlePrintClick}>
            <IconedContent icon="print">
                {localize(/* @kp-localization commons.tisk */ 'commons.tisk')}
            </IconedContent>
        </KpButton>

        <!-- Export button -->
        <KpLinksMenu links={model.exportLinks}
                     dropdownButtonText="Export"
                     singleButtonOverwriteLinkText="Export"
                     icon="sign-in-alt"
                     additionalClasses="export-btn document-export"/>

        <!-- Search at different place button -->
        <KpLinksMenu links={model.searchElsewhereLinks}
                     dropdownButtonText={localize(/* @kp-localization detail.vyhledatJinde */ 'detail.vyhledatJinde')}
                     forceDropdown={true}
                     showIfEmpty={false}
                     icon="search"
                     additionalClasses="vyhledatJinde"/>

        <!-- Show at different place button -->
        <KpLinksMenu links={model.externalDetailLinks}
                     dropdownButtonText="Zobrazit na"
                     singleButtonPrefixText="Zobrazit na "
                     showIfEmpty={false}
                     icon="eye"
                     additionalClasses="document-external-detail-links"/>
    </div>

    <!-- Permalink -->
    <RecordPermalink record={model.record}/>

    <!-- Editation of record cover -->
    <div class="cover-edit-container">
        <!-- Search for cover button -->
        {#if model.filesManageable}
            <KpActionRequestButton path="/api/records/covers/search"
                                   requestMethod="post"
                                   requestBody="{{record: model.record.id}}"
                                   on:success={() => service.refreshPage()}>

                <IconedContent icon="refresh">
                    {localize(/* @kp-localization detail.ZnovuvyhledatObalku */ 'detail.ZnovuvyhledatObalku')}
                </IconedContent>
            </KpActionRequestButton>
        {/if}

        <!-- Delete cover button -->
        {#if exists(model.record.cover) && model.filesManageable}
            <KpActionRequestButton path="/api/records/covers/primary/delete"
                                   requestMethod="post"
                                   requestBody="{{record: model.record.id}}"
                                   buttonStyle="danger"
                                   on:success={() => service.refreshPage()}>

                <IconedContent icon="trash">
                    {localize(/* @kp-localization detail.SmazatObalku */ 'detail.SmazatObalku')}
                </IconedContent>
            </KpActionRequestButton>
        {/if}

        <!-- Upload cover form -->
        {#if !exists(model.record.cover) && model.filesManageable}
            <form class="upload-cover-form" on:submit|preventDefault={handleCoverImageSelect}>
                <label class="upload-cover">
                    <IconedContent icon="upload">
                        {localize(/* @kp-localization detail.nahratObalku */ 'detail.nahratObalku')}
                    </IconedContent>

                    <input class="image-input sr-only"
                           type="file"
                           name="fileData"
                           accept="image/*"
                           bind:files={coverInputFileList}>

                </label>
            </form>
        {/if}
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .action-buttons-container {
        display: flex;
        flex-direction: column;
        gap: @spacing-sm;

        .links-and-actions-container,
        .cover-edit-container {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: @spacing-s;
        }

        .upload-cover-form {
            .upload-cover {
                cursor: pointer;
                height: 34px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 0;
                color: white;
                padding: 0 @spacing-ml;
                border-radius: @border-radius-default;
                background-color: var(--accent-blue);
                border: 1px solid rgba(0, 0, 0, 0.2);
                outline: 1px solid transparent;
                outline-offset: 1px;
                transition: outline-color 0.3s ease-in-out, opacity 0.3s ease-in-out;

                &:hover,
                &:focus {
                    opacity: 0.75;
                    outline-color: var(--accent-blue);
                }
            }
        }
    }
</style>
